// ─── STATE SHAPE ─────────────────────────────────────────────────

export interface IWhatsAppContact {
  recipient_number: string;
  recipient_name: string;
  content: ILastMessage[];
  create_ts?: string | null;
  insert_ts?: string | null;
  timestamp?: string | null;
  unread_message?: number;
  message_status?: string;
}

export interface IWhatsAppContactMetadata {
  RECEPIENT_NUMBER_ACCESSED_BY: string | null;
  WHATSAPP_NUMBER: string | null;
}

export interface IWhatsappMsgMediaHistory {
  attachments: string[];
}

export interface IActionEvent {
  conversation_sentiment_unique_id: number;
  conversation_sentiment_uuid: string;
  conversation_id: string;
  sentiment: string;
  message: string;
  status: string;
  company_outlet_uuid: string;
  created_by_uuid: string | null;
  created_by_name: string | null;
  modified_by_uuid: string | null;
  modified_by_name: string | null;
  create_ts: string;
  insert_ts: string;
}

export interface ILastMessage {
  [key: string]: any;
}

export interface IWhatsAppActivity {
  sender_phone: string;
  sender_name: string;
  social_media_conversation_id: number;
  social_media_conversation_unique_id: number;
  social_media_conversation_uuid: string;
  conversation_id: string;
  socail_media_name: string;
  module_name: string;
  from_number: string;
  recipient_number: string;
  recipient_name?: string;
  message?: string;
  to_number: string;
  message_type: string;
  reply_from_conversation_id: string | null;
  content_type: string;
  content: (
    | { text: string; type: "BODY"; is_restricted?: number | boolean }
    | { type: "BUTTONS"; text?: string; buttons: { text: string; type: string }[] }
    | { type: "HEADER"; text: string; format: "DOCUMENT" | "IMAGE"; example: { header_handle: string[] } }
    | { type: "FOOTER"; text: string }
  )[];
  attachments: any;
  raw_payload: any;
  is_automatic_message: number;
  is_restricted?: number | boolean;
  created_by_uuid: string | null;
  created_by_name: string | null;
  modified_by_uuid: string | null;
  modified_by_name: string | null;
  status: "SENT" | "READ" | "INITIATE" | "DELIVERED" | "SERVICE_OFF" | string;
  create_ts: string;
  insert_ts: string;
}

export interface IWhatsAppActivityPayload {
  message?: string;
  fromNumber: string | null;
  toNumber: string;
  is_automatic_message: number;
  reply_from_conversation_id?: string | null;
  templateName?: string | null;
  company_outlet_uuid: string;
  company_outlet_name: string;
  whatsapp_web_service?: boolean | null;
  objectVariables?: {
    bodyVar?: { type: string; value: string }[];
    buttonVar?: { sub_type: string | null; type: string | null; value: string | null; index?: number | null; payload_type?: string | null }[] | null;
  };
  image?: { link: string };
  document?: { link: string; caption?: string };
  video?: { link: string; caption?: string };
  location?: { latitude: number; longitude: number; name: string; address: string };
  attachments?: any[] | null;
  reference_uuid_column_name?: string;
  reference_uuid_column_value?: string;
}

export interface IRestrictWhatsAppMessagePayload {
  restricted_words_uuid: string | null;
  word: string;
  company_outlet_uuid: string;
  status: "ACTIVE" | "INACTIVE";
}

export type LoadingState = "idle" | "loading" | "loaded" | "failed";

export interface IContactAssignee {
  user_data_scope_unique_id: number;
  user_data_scope_uuid: string;
  table_name: string;
  table_identifier_column_name: string;
  table_identifier_column_value: string;
  table_identifier_reference_value: string;
  module_name: string;
  assigned_to_uuid: string;
  assigned_to_name: string;
  assigned_for_days: number;
  company_outlet_uuid: string;
  status: "ACTIVE" | "INACTIVE";
  type: "NEW" | "REMOVED" | "EXISTING";
  readonly assigned_by_uuid?: string;
  readonly assigned_by_name?: string;
  readonly assigned_at?: string;
  readonly expires_at?: string;
  readonly created_by_uuid?: string;
  readonly created_by_name?: string;
  readonly modified_by_uuid?: string;
  readonly modified_by_name?: string;
  readonly create_ts?: string;
  readonly insert_ts?: string;
}

export interface IModuleSetting {
  master_module_setting_id: number;
  master_module_setting_unique_id: number;
  master_module_setting_uuid: string;
  module_name: string;
  setting_name: string;
  setting_key: string;
  value_datatype: string;
  default_value: string;
  is_encrypted: number;
  show_only_to_enr_admin: number;
  status: string;
  created_by_uuid: string;
  created_by_name: string | null;
  modified_by_uuid: string;
  modified_by_name: string | null;
  create_ts: string;
  insert_ts: string;
  setting_value: string | null;
  company_outlet_module_setting_uuid: string | null;
  company_outlet_uuid: string;
}

export interface IWhatsAppState {
  contactList: {
    list: IWhatsAppContact[];
    loading: boolean;
    error: string | null;
    totalRecords: number;
    hasMoreRecords: boolean;
    message: string;
    meta: IWhatsAppContactMetadata | {};
  };
  actionEventList: {
    list: IActionEvent[];
    loading: LoadingState;
    error: string | null;
    totalRecords: number;
    hasMoreRecords: boolean;
  };
  mediaHistory: {
    list: IWhatsappMsgMediaHistory[];
    loading: LoadingState;
    error: string | null;
    currentRecords: number;
  };
  activeChat: IWhatsAppContact | null;
  contactAssignees: {
    list: IContactAssignee[];
    loading: LoadingState;
    error: string | null;
  };
  moduleSettings: {
    list: IModuleSetting[];
    loading: LoadingState;
  };
}

// ─── ACTION TYPES (discriminated union) ──────────────────────────

export type WhatsAppAction =
  // Contact List
  | { type: "CONTACTS_LOADING" }
  | { type: "CONTACTS_SUCCESS"; payload: { data: IWhatsAppContact[]; totalRecords: number; message: string; meta: IWhatsAppContactMetadata } }
  | { type: "CONTACTS_FAILED"; payload: { error: string } }
  | { type: "CONTACTS_NOT_SCANNED" }
  | { type: "CONTACTS_SERVICE_OFF" }
  | { type: "CONTACTS_CLEAR" }
  | { type: "CONTACTS_SET"; payload: { contacts: IWhatsAppContact[] } }
  | { type: "SET_ACTIVE_CHAT"; payload: IWhatsAppContact | null }

  // Action Events
  | { type: "EVENTS_LOADING" }
  | { type: "EVENTS_SUCCESS"; payload: { data: IActionEvent[]; totalRecords: number } }
  | { type: "EVENTS_FAILED"; payload: { error: string } }
  | { type: "EVENTS_CLEAR" }

  // Media History
  | { type: "MEDIA_LOADING" }
  | { type: "MEDIA_SUCCESS"; payload: { data: IWhatsappMsgMediaHistory[]; currentRecords: number } }
  | { type: "MEDIA_FAILED"; payload: { error: string } }

  // Contact Assignees
  | { type: "ASSIGNEES_LOADING" }
  | { type: "ASSIGNEES_SUCCESS"; payload: { data: IContactAssignee[] } }
  | { type: "ASSIGNEES_FAILED"; payload: { error: string } }

  // Module Settings
  | { type: "MODULE_SETTINGS_LOADING" }
  | { type: "MODULE_SETTINGS_SUCCESS"; payload: { data: IModuleSetting[] } }
  | { type: "MODULE_SETTINGS_FAILED" };

// ─── INITIAL STATE ───────────────────────────────────────────────

export const initialWhatsAppState: IWhatsAppState = {
  contactList: {
    list: [],
    loading: false,
    error: null,
    totalRecords: 0,
    hasMoreRecords: false,
    message: "",
    meta: {},
  },
  actionEventList: {
    list: [],
    loading: "idle",
    error: null,
    totalRecords: 0,
    hasMoreRecords: false,
  },
  mediaHistory: {
    list: [],
    loading: "idle",
    error: null,
    currentRecords: 0,
  },
  activeChat: null,
  contactAssignees: {
    list: [],
    loading: "idle",
    error: null,
  },
  moduleSettings: {
    list: [],
    loading: "idle",
  },
};
